// src/components/charts/WorstIdleUnitChart.jsx
'use client';

import { useRef, useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import Chart from 'react-apexcharts';
import { useLanguage } from '../../../../utils/i18n';

const PRIMARY = '#485D9A';

export default function WorstIdleUnitChart({ categories = [], series = [], isLoading = false }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(1200);
  const { t } = useLanguage();

  useEffect(() => {
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(e.contentRect.width);
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const isWide = width >= 1000;

  const dataValues = series.length > 0 && series[0]?.data ? series[0].data : [];
  const maxValue = dataValues.length > 0 ? Math.max(...dataValues) : 0;

  const formatTime = (val) => {
    if (!val) return '0m';
    const hours = Math.floor(val);
    const minutes = Math.round((val - hours) * 60);

    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  const options = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      animations: { enabled: true }
    },
    noData: {
      text: isLoading ? ' ' : t('revenue.noDataAvailable', 'No Data Available'),
      align: 'center',
      verticalAlign: 'middle',
      style: {
        color: '#888',
        fontSize: '14px'
      }
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: isWide ? '55%' : '65%',
        borderRadius: 4, // Diubah ke 4 agar ujung bar tidak terlalu melengkung
        dataLabels: {
          position: 'top'
        }
      }
    },

    dataLabels: {
      enabled: true,
      // Format desimal menjadi jam dan menit yang lebih informatif
      formatter: (val) => formatTime(val),
      offsetY: -20,
      style: {
        colors: ['#333'],
        fontSize: '12px',
        fontWeight: 700
      }
    },

    xaxis: {
      categories: categories,
      labels: {
        rotate: isWide ? 0 : -45,
        style: { fontSize: '11px', fontWeight: 600, colors: '#666' }
      },
      axisBorder: { show: false },
      axisTicks: { show: false }
    },

    yaxis: {
      min: 0,
      // Beri ruang 15% di atas chart agar label angka tidak terpotong
      max: maxValue > 0 ? Math.ceil(maxValue + maxValue * 0.15) : 10,
      labels: {
        style: { fontSize: '12px' },
        formatter: (val) => val.toFixed(0) // Sumbu Y tetap pakai bilangan bulat agar rapi
      }
    },

    grid: {
      borderColor: '#D8D8D8',
      strokeDashArray: 6,
      yaxis: {
        lines: { show: true }
      }
    },

    colors: [PRIMARY],

    tooltip: {
      y: {
        formatter: (val) => formatTime(val)
      }
    }
  };

  return (
    <div ref={containerRef}>
      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0px 6px 20px rgba(0,0,0,0.08)'
        }}
      >
        <CardContent>
          <Typography variant="h4" mb={2} color="text.primary" fontWeight="bold">
            {t('revenue.topWorstIdle', 'Worst 10 Idle Unit')}
          </Typography>
          {isLoading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300
              }}
            >
              <CircularProgress sx={{ color: PRIMARY, mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {t('revenue.calculateTopWorstIdle', 'Calculating Worst 10 Idle Unit...')}
              </Typography>
            </Box>
          ) : (
            <Chart options={options} series={series} type="bar" height={300} />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
